<template>
  <div class="apt-hx">
    <div class="apt-hx-toolbar">
      <span class="apt-hx-count">
        <template v-if="total !== null">
          {{ rawAptHx.length }} จาก {{ total }} รายการ · ล่าสุดก่อน
        </template>
      </span>
      <date-picker
        v-model="dateRangeAptHx"
        class="apt-hx-date"
        type="date"
        range
        format="D MMM YY"
        :shortcuts="dateRangeShortcuts"
        placeholder="ทุกช่วงวันที่"
        input-class="form-control form-control-sm"
      ></date-picker>
      <label class="apt-hx-search">
        <i class="fas fa-search" aria-hidden="true"></i>
        <span class="sr-only">ค้นหา</span>
        <input
          v-model="filterAptHx"
          type="search"
          placeholder="ค้นหาแพทย์ หรือรายการนัดหมาย"
        />
      </label>
    </div>

    <div ref="scrollBox" class="apt-hx-scroll">
      <table class="apt-hx-table">
        <colgroup>
          <col style="width: 104px" />
          <col style="width: 56px" />
          <col style="width: 132px" />
          <col />
          <col style="width: 180px" />
          <col style="width: 112px" />
          <col v-if="showCheckin" style="width: 124px" />
          <col style="width: 76px" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="pl-edge">วันที่ ↓</th>
            <th scope="col">เวลา</th>
            <th scope="col">แพทย์</th>
            <th scope="col">รายการนัดหมาย</th>
            <th scope="col">โน๊ตเพิ่มเติม</th>
            <th scope="col">สถานะนัด</th>
            <th v-if="showCheckin" scope="col">เวลามาถึง</th>
            <th scope="col" class="pr-edge">
              <span class="sr-only">การทำงาน</span>
            </th>
          </tr>
        </thead>
        <tbody v-if="isBusyAptHx">
          <tr>
            <td :colspan="colCount" class="apt-hx-state">
              <b-spinner small class="text-primary"></b-spinner>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!filteredAptHxList.length">
          <tr>
            <td :colspan="colCount" class="apt-hx-state">
              {{ filterAptHx ? "ไม่พบนัดหมายที่ตรงกับคำค้น" : "ไม่มีนัดหมาย" }}
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="item in filteredAptHxList"
            :key="item.id"
            :class="{
              'is-today': item.timing === 'today' && !item.deletedAt,
              'is-deleted': !!item.deletedAt,
            }"
          >
            <td class="pl-edge apt-hx-date-cell">{{ formatDate(item.date) }}</td>
            <td class="apt-hx-time">{{ formatTime(item.date) }}</td>
            <td class="apt-hx-ellipsis" :title="item.doctor">
              {{ item.doctor }}
            </td>
            <td>
              <div class="apt-hx-chips">
                <span v-if="item.tag" class="apt-hx-tag" :class="item.tag.cls">
                  {{ item.tag.label }}
                </span>
                <span
                  v-for="(name, i) in item.titleItems"
                  :key="i"
                  class="apt-hx-chip"
                  >{{ name }}</span
                >
              </div>
            </td>
            <td class="apt-hx-ellipsis apt-hx-note" :title="item.note">
              {{ item.note || "—" }}
            </td>
            <td>
              <span class="apt-hx-pill" :class="statusClass(item.statusId)">
                {{ item.aptStatus }}
              </span>
            </td>
            <td v-if="showCheckin">
              <span
                v-if="item.checkinStatusName"
                class="apt-hx-pill"
                :class="checkinClass(item)"
                >{{ item.checkinStatusName }}</span
              >
              <span v-else class="apt-hx-muted">—</span>
            </td>
            <td class="pr-edge">
              <div class="apt-hx-actions">
                <button
                  v-b-tooltip.hover
                  type="button"
                  title="ประวัติการแก้ไข"
                  aria-label="ประวัติการแก้ไข"
                  @click="openHistory(item)"
                >
                  <i class="fas fa-history" aria-hidden="true"></i>
                </button>
                <button
                  v-if="
                    onQueueManageRoute &&
                    !item.deletedAt &&
                    !isOtherBranch(item.branchId)
                  "
                  v-b-tooltip.hover
                  type="button"
                  class="is-accent"
                  title="ไปที่นัดหมาย"
                  aria-label="ไปที่นัดหมาย"
                  @click="toApt(item)"
                >
                  <i class="fas fa-external-link-square-alt" aria-hidden="true"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div ref="sentinel" class="apt-hx-footer">
        <template v-if="isLoadingMore">
          <b-spinner small class="text-primary mr-2"></b-spinner>
          กำลังโหลดเพิ่ม…
        </template>
        <b-button
          v-else-if="loadError"
          size="sm"
          variant="link"
          @click="loadNextPage"
        >
          โหลดไม่สำเร็จ ลองใหม่
        </b-button>
        <span v-else-if="!hasMore && rawAptHx.length">
          แสดงครบ {{ rawAptHx.length }} รายการ
        </span>
      </div>
    </div>
    <AppointmentEditingHistoryModal ref="appointmentEditingHistoryModal" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { eventBus } from "@/main";
import CrossBranchMixin from "@/mixins/CrossBranchMixin";
import AppointmentEditingHistoryModal from "@/components/modal/AppointmentEditingHistory";

// appointment statusId → pill style (ids: dent-cloud-service src/constants APPT_STATUS_*)
const STATUS_CONFIRMED = [2, 21];
const STATUS_DONE = [5, 6, 7, 11, 12];
const STATUS_CANCELLED = [23];

// checkinStatus → pill style; no checkinStatus but a name means "ไม่มา"
const CHECKIN_CLASS = { 1: "is-ok", 2: "is-early", 3: "is-late", 4: "is-walkin" };

export default {
  name: "AppointmentHistoryTable",
  components: { AppointmentEditingHistoryModal },
  mixins: [CrossBranchMixin],
  props: ["patient", "mode"],
  data() {
    return {
      isBusyAptHx: false,
      isLoadingMore: false,
      isFetching: false,
      loadError: false,
      rawAptHx: [],
      total: null,
      pageSize: 30,
      requestId: 0,
      observer: null,
      filterAptHx: null,
      dateRangeAptHx: {},
    };
  },
  computed: {
    ...mapGetters({
      formatAptHxList: "moduleAppointment/formatAptHxList",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
    }),
    showCheckin() {
      return this.mode !== "unnamed";
    },
    colCount() {
      return this.showCheckin ? 8 : 7;
    },
    hasMore() {
      return this.total === null || this.rawAptHx.length < this.total;
    },
    filteredAptHxList() {
      // date range is filtered server-side (see dateParams); search runs on loaded rows
      const q = (this.filterAptHx || "").trim().toLowerCase();
      const today = moment();
      return this.formatAptHxList(this.rawAptHx)
        .filter(
          (apt) =>
            !q ||
            [apt.doctor, apt.title, apt.note, apt.aptStatus, apt.branchName]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(q))
        )
        .map((apt) => {
          let timing = "past";
          if (moment(apt.date).isSame(today, "day")) timing = "today";
          else if (moment(apt.date).isAfter(today, "day")) timing = "future";
          return { ...apt, timing, tag: this.rowTag(apt, timing) };
        });
    },
    dateParams() {
      const [from, to] = Array.isArray(this.dateRangeAptHx)
        ? this.dateRangeAptHx
        : [];
      if (!from || !to) return {};
      return {
        startDt: moment(from).startOf("day").toISOString(),
        endDt: moment(to).endOf("day").toISOString(),
      };
    },
    onQueueManageRoute() {
      return this.$route.name === "QueueManage";
    },
  },
  methods: {
    ...mapActions({
      fetchAppointmentHistoryPage:
        "moduleAppointment/fetchAppointmentHistoryPage",
    }),
    rowTag(apt, timing) {
      if (apt.deletedAt) return { label: "ลบแล้ว", cls: "is-deleted" };
      if (this.isOtherBranch(apt.branchId))
        return { label: apt.branchName || "สาขาอื่น", cls: "is-branch" };
      if (timing === "today") return { label: "วันนี้", cls: "is-today" };
      if (timing === "future") return { label: "นัดล่วงหน้า", cls: "is-future" };
      return null;
    },
    statusClass(statusId) {
      if (STATUS_CONFIRMED.includes(statusId)) return "is-confirmed";
      if (STATUS_DONE.includes(statusId)) return "is-done";
      if (STATUS_CANCELLED.includes(statusId)) return "is-cancelled";
      return "is-pending";
    },
    checkinClass(item) {
      return CHECKIN_CLASS[item.checkinStatus] || "is-noshow";
    },
    resetAndLoad() {
      // bump requestId so any in-flight page for the old query is discarded
      this.requestId += 1;
      this.rawAptHx = [];
      this.total = null;
      this.loadError = false;
      this.isLoadingMore = false;
      this.isFetching = false;
      if (this.$refs.scrollBox) this.$refs.scrollBox.scrollTop = 0;
      this.loadNextPage();
    },
    async loadNextPage() {
      if (!this.patient?.id || this.isFetching || !this.hasMore) return;
      this.isFetching = true;
      const requestId = this.requestId;
      const isFirstPage = this.rawAptHx.length === 0;
      this.isLoadingMore = !isFirstPage;
      this.isBusyAptHx = isFirstPage;
      this.loadError = false;
      try {
        const { pagination, appointments } =
          await this.fetchAppointmentHistoryPage({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            patientId: this.patient.id,
            includeDeleted: true,
            offset: this.rawAptHx.length,
            limit: this.pageSize,
            ...this.dateParams,
          });
        if (requestId !== this.requestId) return;
        this.rawAptHx = this.rawAptHx.concat(appointments || []);
        // an empty page means the server has nothing more, whatever total says
        this.total = appointments?.length ? pagination.total : this.rawAptHx.length;
      } catch (err) {
        if (requestId !== this.requestId) return;
        console.error(err);
        this.loadError = true;
      } finally {
        if (requestId === this.requestId) {
          this.isFetching = false;
          this.isLoadingMore = false;
          this.isBusyAptHx = false;
          this.recheckSentinel();
        }
      }
    },
    recheckSentinel() {
      // re-observing fires the callback immediately, so a page that doesn't fill
      // the box (or a search that hides most rows) keeps loading until it does
      this.$nextTick(() => {
        const el = this.$refs.sentinel;
        if (!this.observer || !el) return;
        this.observer.unobserve(el);
        this.observer.observe(el);
      });
    },
    setupObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting) && !this.loadError) {
            this.loadNextPage();
          }
        },
        { root: this.$refs.scrollBox, rootMargin: "0px 0px 200px 0px" }
      );
      this.observer.observe(this.$refs.sentinel);
    },
    toApt(e) {
      eventBus.$emit("blinkAptId", e.id);
      eventBus.$emit("goToDateTime", e.date);
      this.$bvModal.hide("ProfileCustomerModal");
      this.$bvModal.hide("EventModal");
    },
    openHistory(item) {
      if (item && item.id) {
        this.$refs.appointmentEditingHistoryModal.show(item.id);
      }
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatTime(date) {
      if (date) {
        return moment(date).locale("th").format("HH:mm");
      } else {
        return "";
      }
    },
  },
  watch: {
    dateRangeAptHx() {
      this.resetAndLoad();
    },
    "patient.id"() {
      this.resetAndLoad();
    },
  },
  async mounted() {
    await this.$nextTick();
    this.setupObserver();
    this.resetAndLoad();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
};
</script>

<style scoped>
.apt-hx {
  --hx-accent: #0e6c8c;
  --hx-accent-tint: #eaf4f8;
  --hx-text: #1c2430;
  --hx-muted: #5b6573;
  --hx-faint: #6b7480;
  --hx-line: #eef0f3;
  --hx-line-strong: #e1e5ea;
  background: #fff;
  border: 1px solid var(--hx-line-strong);
  border-radius: 10px;
  overflow: hidden;
  color: var(--hx-text);
}

.apt-hx-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #e8ebef;
}
.apt-hx-count {
  margin-right: auto;
  font-size: 12px;
  color: var(--hx-muted);
}
.apt-hx-search {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  width: 260px;
  max-width: 100%;
  margin: 0;
  padding: 0 10px;
  border: 1px solid #d5dae0;
  border-radius: 6px;
  color: var(--hx-muted);
  font-size: 13px;
}
.apt-hx-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--hx-text);
}
/* Base.scss forces .mx-datepicker-range to 100% width, which pushed the picker
   onto its own row; keep it a compact control beside the search box */
.apt-hx-toolbar .apt-hx-date {
  width: 230px !important;
  max-width: 100%;
}
.apt-hx-date ::v-deep .mx-input {
  height: 32px;
  border-color: #d5dae0;
  border-radius: 6px;
  font-size: 13px;
  box-shadow: none;
}

.apt-hx-scroll {
  max-height: 60vh;
  overflow: auto;
}
.apt-hx-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
  line-height: 1.45;
}
.apt-hx-table th,
.apt-hx-table td {
  padding: 5px 8px;
  vertical-align: middle;
}
.apt-hx-table td {
  border-bottom: 1px solid var(--hx-line);
}
.apt-hx-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8f9fb;
  border-bottom: 1px solid var(--hx-line-strong);
  font-size: 12px;
  font-weight: 600;
  color: #4a535e;
  text-align: left;
  white-space: nowrap;
}
.apt-hx-table .pl-edge {
  padding-left: 16px;
}
.apt-hx-table .pr-edge {
  padding-right: 16px;
}
.apt-hx-state {
  padding: 24px 16px !important;
  text-align: center;
  color: var(--hx-muted);
}

.apt-hx-date-cell {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.apt-hx-time {
  color: #4a535e;
  font-variant-numeric: tabular-nums;
}
.apt-hx-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.apt-hx-note {
  font-size: 12px;
  color: var(--hx-muted);
}
.apt-hx-muted {
  color: var(--hx-faint);
}

.apt-hx-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.apt-hx-chip {
  padding: 0 6px;
  border-radius: 4px;
  background: #f1f3f5;
  font-size: 12px;
}
.apt-hx-tag {
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11.5px;
  font-weight: 500;
  white-space: nowrap;
}
.apt-hx-tag.is-future {
  background: #e9f1f8;
  color: #1d4f91;
}
.apt-hx-tag.is-today {
  background: var(--hx-accent);
  color: #fff;
}
.apt-hx-tag.is-branch {
  background: #eef0f2;
  color: #4a535e;
}
.apt-hx-tag.is-deleted {
  border: 1px solid #c9d0d8;
  color: #4a535e;
  text-decoration: none;
}

.apt-hx-pill {
  display: inline-block;
  max-width: 100%;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 11.5px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.apt-hx-pill.is-pending {
  border: 1px dashed #a9b2bc;
  color: #4a535e;
}
.apt-hx-pill.is-confirmed {
  border-color: #c9d0d8;
  background: #fff;
}
.apt-hx-pill.is-done {
  background: #eef0f2;
  color: #2e3742;
}
.apt-hx-pill.is-cancelled {
  color: var(--hx-faint);
  padding-left: 0;
}
.apt-hx-pill.is-ok {
  background: #e3f3ea;
  color: #17633f;
}
.apt-hx-pill.is-early {
  background: #e5eefa;
  color: #1d4f91;
}
.apt-hx-pill.is-late {
  background: #fcebd9;
  color: #8a4300;
}
.apt-hx-pill.is-walkin {
  background: #efe9fa;
  color: #5b3e96;
}
.apt-hx-pill.is-noshow {
  background: #fbe7e7;
  color: #8e2a2a;
}

.apt-hx-actions {
  display: flex;
  justify-content: flex-end;
  gap: 2px;
}
.apt-hx-actions button {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4a535e;
  cursor: pointer;
}
.apt-hx-actions button.is-accent {
  color: var(--hx-accent);
}
.apt-hx-actions button:hover {
  background: #eef0f3;
}

tr.is-today td {
  background: var(--hx-accent-tint);
}
tr.is-today .apt-hx-chip {
  background: #fff;
  border: 1px solid var(--hx-line-strong);
}
tr.is-deleted td {
  background: #f8f9fa;
  color: var(--hx-faint);
}
tr.is-deleted .apt-hx-date-cell,
tr.is-deleted .apt-hx-time,
tr.is-deleted .apt-hx-ellipsis,
tr.is-deleted .apt-hx-chip {
  text-decoration: line-through;
}
tr.is-deleted .apt-hx-chip {
  background: transparent;
  padding: 0;
}

.apt-hx-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 6px;
  font-size: 12px;
  color: var(--hx-muted);
}
</style>
